import type { PackingSlipDocument, PickListDocument } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate, formatDateTime, formatQty } from '../../../lib/format';

/**
 * A4 print views for the pick list and the packing slip.
 *
 * Two rules, both from ARCHITECTURE §9 / the module brief:
 *  1. **Printouts stay Swedish in every UI language.** The picker on the floor
 *     and the driver reading the slip are Swedish-speaking regardless of what
 *     the account language is set to, so these labels come from the
 *     `pickingShipping:print.*` subtree, which is Swedish in sv, en AND zh.
 *  2. The line order is the SERVER's order (`sortKey`). Never re-sort here —
 *     that would silently desynchronise paper from the scanner, which is the
 *     one thing the hybrid design must not do.
 */

export function PickListPrintView({ document }: { document: PickListDocument }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <article className="bg-white p-6 text-black print:p-0" data-testid="pick-list-print">
      <header className="mb-4 border-b border-black/20 pb-3">
        <h1 className="font-mono text-xl font-bold tracking-wide">{t('print.pickListTitle')}</h1>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
          <Field label={t('print.round')} value={document.roundNo} mono />
          <Field label={t('print.deliveryDate')} value={formatDate(document.deliveryDate)} />
          <Field label={t('print.customer')} value={document.restaurants.join(', ')} />
          <Field label={t('print.printedAt')} value={formatDateTime(document.printedAt)} />
        </dl>
      </header>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-black/40 text-left">
            <th className="py-1 pr-2">{t('print.position')}</th>
            <th className="py-1 pr-2">{t('print.binCode')}</th>
            <th className="py-1 pr-2">{t('print.articleNo')}</th>
            <th className="py-1 pr-2">{t('print.articleName')}</th>
            <th className="py-1 pr-2">{t('print.lot')}</th>
            <th className="py-1 pr-2">{t('print.bestBefore')}</th>
            <th className="py-1 pr-2 text-right">{t('print.quantity')}</th>
            <th className="py-1 pr-2">{t('print.unit')}</th>
            <th className="w-20 py-1">{t('print.picked')}</th>
          </tr>
        </thead>
        <tbody>
          {document.lines.map((line) => (
            <tr key={`${line.sortKey}-${line.articleNo}-${line.orderNo}`} className="border-b border-black/10">
              <td className="py-1 pr-2 font-mono">{line.sortKey + 1}</td>
              <td className="py-1 pr-2 font-mono font-semibold">{line.binCode ?? '—'}</td>
              <td className="py-1 pr-2 font-mono">{line.articleNo}</td>
              <td className="py-1 pr-2">{line.articleName}</td>
              <td className="py-1 pr-2">{line.lotNo ?? '—'}</td>
              <td className="py-1 pr-2">{line.bestBefore ?? '—'}</td>
              <td className="py-1 pr-2 text-right font-mono font-semibold">{formatQty(line.qtyBase)}</td>
              <td className="py-1 pr-2">{line.unitCode}</td>
              {/* Deliberately blank: this is the box the picker writes in. */}
              <td className="py-1">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="mt-8 flex items-end gap-8 text-sm">
        <div className="flex-1 border-t border-black/40 pt-1">{t('print.signature')}</div>
        <div className="w-40 border-t border-black/40 pt-1">{t('print.deliveryDate')}</div>
      </footer>
    </article>
  );
}

export function PackingSlipPrintView({ document }: { document: PackingSlipDocument }): ReactNode {
  const { t } = useTranslation('pickingShipping');
  return (
    <article className="bg-white p-6 text-black print:p-0" data-testid="packing-slip-print">
      <header className="mb-4 flex items-start justify-between gap-6 border-b border-black/20 pb-3">
        <div>
          <h1 className="font-mono text-xl font-bold tracking-wide">{t('print.packingSlipTitle')}</h1>
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <Field label={t('print.order')} value={document.orderNo} mono />
            <Field label={t('print.deliveryDate')} value={formatDate(document.deliveryDate)} />
            <Field label={t('print.customer')} value={document.restaurantName} />
            <Field label={t('print.printedAt')} value={formatDateTime(document.printedAt)} />
          </dl>
          <address className="mt-2 text-sm not-italic">
            {document.deliveryAddress.map((line) => (
              <span key={line} className="block">
                {line}
              </span>
            ))}
          </address>
        </div>
        <div className="w-44 shrink-0 text-right">
          <span className="block font-mono text-lg font-bold">{document.shipmentNo}</span>
          {/* The QR IMAGE is rendered by the print service (M9) from this payload;
              the URL is printed as text so the slip is usable either way. */}
          <span className="mt-2 block text-[10px] leading-tight">{t('print.scanToReceive')}</span>
          <span className="block break-all text-[10px] leading-tight" data-testid="packing-slip-qr-url">
            {document.qrUrl}
          </span>
        </div>
      </header>

      {document.pallets.length > 0 ? (
        <p className="mb-3 text-sm">
          <span className="font-semibold">{t('print.pallets')}:</span>{' '}
          {document.pallets.map((pallet) => `${pallet.lpn} (${pallet.packageCount})`).join(', ')}
        </p>
      ) : null}

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-black/40 text-left">
            <th className="py-1 pr-2">{t('print.articleNo')}</th>
            <th className="py-1 pr-2">{t('print.articleName')}</th>
            <th className="py-1 pr-2 text-right">{t('print.quantity')}</th>
            <th className="py-1 pr-2">{t('print.unit')}</th>
          </tr>
        </thead>
        <tbody>
          {document.lines.map((line) => (
            <tr key={`${line.articleNo}-${line.sortKey}`} className="border-b border-black/10">
              <td className="py-1 pr-2 font-mono">{line.articleNo}</td>
              <td className="py-1 pr-2">{line.articleName}</td>
              <td className="py-1 pr-2 text-right font-mono font-semibold">{formatQty(line.qtyBase)}</td>
              <td className="py-1 pr-2">{line.unitCode}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="mt-8 border-t border-black/40 pt-1 text-sm">{t('print.signature')}</footer>
    </article>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-black/60">{label}</dt>
      <dd className={mono ? 'font-mono font-semibold' : 'font-medium'}>{value || '—'}</dd>
    </div>
  );
}
